import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminService } from '../../services/adminService';
import './AdminTickets.css';

const TICKET_STATUS = {
  OPEN: { label: 'Open', color: '#3b82f6' },
  IN_PROGRESS: { label: 'In Progress', color: '#f59e0b' },
  AWAITING_CUSTOMER: { label: 'Awaiting Customer', color: '#8b5cf6' },
  RESOLVED: { label: 'Resolved', color: '#10b981' },
  CLOSED: { label: 'Closed', color: '#6b7280' },
};

const TICKET_PRIORITY = {
  LOW: { label: 'Low', color: '#6b7280' },
  NORMAL: { label: 'Normal', color: '#3b82f6' },
  HIGH: { label: 'High', color: '#f59e0b' },
  URGENT: { label: 'Urgent', color: '#ef4444' },
};

const AdminTickets = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState(null);

  // Reply state
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Update state
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchStats();
    if (ticketId) {
      fetchTicketById(ticketId);
    } else {
      fetchTickets();
    }
  }, [ticketId, statusFilter, page]);

  const fetchStats = async () => {
    try {
      const response = await adminService.getTicketStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getTickets(
        page,
        20,
        statusFilter === 'ALL' ? null : statusFilter
      );
      if (response.success) {
        const ticketsData = response.data?.content || response.data;
        setTickets(Array.isArray(ticketsData) ? ticketsData : []);
        setTotalPages(response.data?.totalPages || 1);
      } else {
        setError(response.message || 'Failed to load tickets');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getTicketById(id);
      if (response.success) {
        setSelectedTicket(response.data);
      } else {
        setError(response.message || 'Ticket not found');
      }
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    setUpdating(true);
    try {
      const response = await adminService.updateTicket(selectedTicket.id, { status: newStatus });
      if (response.success) {
        setSelectedTicket(response.data);
        fetchStats();
      }
    } catch (err) {
      console.error('Error updating ticket:', err);
      alert(err.response?.data?.message || 'Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    if (!selectedTicket) return;
    setUpdating(true);
    try {
      const response = await adminService.updateTicket(selectedTicket.id, { priority: newPriority });
      if (response.success) {
        setSelectedTicket(response.data);
      }
    } catch (err) {
      console.error('Error updating priority:', err);
      alert(err.response?.data?.message || 'Failed to update priority');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setSendingReply(true);
    try {
      const response = await adminService.addTicketMessage(selectedTicket.id, replyMessage);
      if (response.success) {
        setSelectedTicket(prev => ({
          ...prev,
          messages: [...(prev.messages || []), response.data],
        }));
        setReplyMessage('');
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      alert(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const statusOptions = [
    { value: 'ALL', label: 'All Tickets' },
    { value: 'OPEN', label: 'Open' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'AWAITING_CUSTOMER', label: 'Awaiting Customer' },
    { value: 'RESOLVED', label: 'Resolved' },
    { value: 'CLOSED', label: 'Closed' },
  ];

  if (loading) {
    return (
      <div className="admin-tickets">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-tickets">
        <div className="admin-error">
          <p>{error}</p>
          <button
            onClick={() => (ticketId ? fetchTicketById(ticketId) : fetchTickets())}
            className="retry-btn"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Ticket detail view
  if (ticketId && selectedTicket) {
    return (
      <div className="admin-tickets">
        <button className="back-btn" onClick={() => navigate('/admin/support')}>
          ← Back to Tickets
        </button>

        <div className="ticket-detail-container">
          <div className="ticket-detail-main">
            <div className="ticket-detail-header">
              <div className="ticket-header-top">
                <div className="ticket-info">
                  <span className="ticket-number">{selectedTicket.ticketNumber}</span>
                  <h2>{selectedTicket.subject}</h2>
                </div>
                <span
                  className="status-badge"
                  style={{ backgroundColor: TICKET_STATUS[selectedTicket.status]?.color }}
                >
                  {TICKET_STATUS[selectedTicket.status]?.label}
                </span>
              </div>
              <div className="ticket-meta-row">
                <span>Category: {selectedTicket.category}</span>
                <span>Created: {new Date(selectedTicket.createdAt).toLocaleString()}</span>
                {selectedTicket.orderNumber && <span>Order: {selectedTicket.orderNumber}</span>}
              </div>
            </div>

            <div className="messages-section">
              <h3>Conversation</h3>
              <div className="messages-list">
                <div className="message customer-message">
                  <div className="message-header">
                    <span className="sender">{selectedTicket.userName || selectedTicket.userEmail}</span>
                    <span className="time">{new Date(selectedTicket.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="message-body">{selectedTicket.description}</div>
                </div>

                {selectedTicket.messages?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`message ${msg.senderType === 'SUPPORT' ? 'support-message' : 'customer-message'}`}
                  >
                    <div className="message-header">
                      <span className="sender">
                        {msg.senderType === 'SUPPORT' ? 'Support Team' : msg.senderName || msg.senderEmail}
                      </span>
                      <span className="time">{new Date(msg.createdAt).toLocaleString()}</span>
                    </div>
                    <div className="message-body">{msg.message}</div>
                  </div>
                ))}
              </div>

              {selectedTicket.status !== 'CLOSED' && (
                <div className="reply-section">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    rows={4}
                    maxLength={5000}
                  />
                  <div className="reply-actions">
                    <span className="char-count">{replyMessage.length}/5000</span>
                    <button
                      className="send-reply-btn"
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyMessage.trim()}
                    >
                      {sendingReply ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="ticket-detail-sidebar">
            <div className="sidebar-section">
              <h4>Customer</h4>
              <p className="customer-name">{selectedTicket.userName || 'N/A'}</p>
              <p className="customer-email">{selectedTicket.userEmail}</p>
            </div>

            <div className="sidebar-section">
              <h4>Status</h4>
              <select
                value={selectedTicket.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={updating}
                className="status-select"
              >
                {Object.entries(TICKET_STATUS).map(([value, { label }]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="sidebar-section">
              <h4>Priority</h4>
              <select
                value={selectedTicket.priority}
                onChange={(e) => handlePriorityChange(e.target.value)}
                disabled={updating}
                className="priority-select"
              >
                {Object.entries(TICKET_PRIORITY).map(([value, { label }]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="sidebar-section">
              <h4>Timeline</h4>
              <div className="timeline-info">
                <div className="timeline-item">
                  <span className="timeline-label">Created</span>
                  <span className="timeline-value">
                    {new Date(selectedTicket.createdAt).toLocaleString()}
                  </span>
                </div>
                {selectedTicket.firstResponseAt && (
                  <div className="timeline-item">
                    <span className="timeline-label">First Response</span>
                    <span className="timeline-value">
                      {new Date(selectedTicket.firstResponseAt).toLocaleString()}
                    </span>
                  </div>
                )}
                {selectedTicket.resolvedAt && (
                  <div className="timeline-item">
                    <span className="timeline-label">Resolved</span>
                    <span className="timeline-value">
                      {new Date(selectedTicket.resolvedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="quick-actions">
              {selectedTicket.status === 'OPEN' && (
                <button
                  className="action-btn start"
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  disabled={updating}
                >
                  Start Working
                </button>
              )}
              {selectedTicket.status === 'IN_PROGRESS' && (
                <>
                  <button
                    className="action-btn await"
                    onClick={() => handleStatusChange('AWAITING_CUSTOMER')}
                    disabled={updating}
                  >
                    Await Customer
                  </button>
                  <button
                    className="action-btn resolve"
                    onClick={() => handleStatusChange('RESOLVED')}
                    disabled={updating}
                  >
                    Mark Resolved
                  </button>
                </>
              )}
              {selectedTicket.status === 'RESOLVED' && (
                <button
                  className="action-btn close"
                  onClick={() => handleStatusChange('CLOSED')}
                  disabled={updating}
                >
                  Close Ticket
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tickets list view
  return (
    <div className="admin-tickets">
      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item open">
            <span className="stat-value">{stats.open}</span>
            <span className="stat-label">Open</span>
          </div>
          <div className="stat-item progress">
            <span className="stat-value">{stats.inProgress}</span>
            <span className="stat-label">In Progress</span>
          </div>
          <div className="stat-item awaiting">
            <span className="stat-value">{stats.awaitingCustomer}</span>
            <span className="stat-label">Awaiting</span>
          </div>
          <div className="stat-item resolved">
            <span className="stat-value">{stats.resolved}</span>
            <span className="stat-label">Resolved</span>
          </div>
        </div>
      )}

      <div className="tickets-header">
        <div className="tickets-filters">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              className={`filter-btn ${statusFilter === option.value ? 'active' : ''}`}
              onClick={() => {
                setStatusFilter(option.value);
                setPage(0);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="tickets-count">Showing {tickets.length} tickets</div>
      </div>

      <div className="tickets-table-container">
        <table className="tickets-table">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Subject</th>
              <th>Customer</th>
              <th>Category</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No tickets found
                </td>
              </tr>
            ) : (
              tickets.map((ticket) => (
                <motion.tr
                  key={ticket.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="ticket-row"
                >
                  <td className="ticket-id">{ticket.ticketNumber}</td>
                  <td className="ticket-subject">{ticket.subject}</td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{ticket.userName || 'N/A'}</div>
                      <div className="customer-email">{ticket.userEmail}</div>
                    </div>
                  </td>
                  <td>{ticket.category}</td>
                  <td>
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: TICKET_PRIORITY[ticket.priority]?.color }}
                    >
                      {TICKET_PRIORITY[ticket.priority]?.label}
                    </span>
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: TICKET_STATUS[ticket.status]?.color }}
                    >
                      {TICKET_STATUS[ticket.status]?.label}
                    </span>
                  </td>
                  <td>{new Date(ticket.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="view-btn"
                      onClick={() => navigate(`/admin/support/${ticket.id}`)}
                    >
                      View
                    </button>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            Previous
          </button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminTickets;
